/*----------------------------------
- DEPENDANCES
----------------------------------*/

import type { LucideIcon } from 'lucide-preact';
import {
    Briefcase,
    Building2,
    Code,
    DollarSign,
    Globe,
    Handshake,
    Users,
    Wrench,
} from 'lucide-preact';

// App
import type { ContactLeadTopic } from '@/common/types/marketing/contactLead';

/*----------------------------------
- TYPES
----------------------------------*/

export type MarketingPageId = 'saas' | 'companies' | 'recruiters' | 'partners' | 'agencies' | 'eor' | 'tools';

export interface MarketingNavItem {
    id?: MarketingPageId;
    label: string;
    href: string;
    description: string;
    icon: LucideIcon;
}

export interface MarketingFooterGroup {
    title: string;
    links: Array<{
        label: string;
        href: string;
    }>;
}

export type MarketingContactHrefOptions = {
    topic?: ContactLeadTopic;
    source?: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const MARKETING_LOGO_URL =
    'https://sitlmwrkxbvpqzfegvww.supabase.co/storage/v1/object/public/static/logoCP.png';

export const MARKETING_URLS = {
    home: '/',
    tools: '/tools',
    platform: '/platform',
    companies: '/companies',
    recruiters: '/recruiters',
    partners: '/partners',
    agencies: '/agencies',
    eorWhitelabel: '/eor-whitelabel',
    affiliateProgram: '/affiliate-program',
    dealPartner: '/deal-partner',
    apiDocs: '/api-docs',
    affiliatesApi: '/api/affiliates',
    recruitmentApi: '/api/recruitment',
    contact: '/contact',
    login: '/login',
    signup: '/inbound/platform',
    saasDemo: '/inbound/saas',
    jordanCalendly: 'https://calendly.com/jordan-crosspath/introcall',
    platformSignup: '/inbound/platform',
    marketingBackground: '/marketing',
    privacy: '/privacy',
    terms: '/terms',
    linkedin: 'https://www.linkedin.com/company/cross-path-network/?viewAsMember=true',
} as const;

export const MARKETING_SOLUTION_ITEMS: MarketingNavItem[] = [
    {
        id: 'recruiters',
        label: 'Marketplace',
        href: MARKETING_URLS.recruiters,
        icon: Briefcase,
        description: 'Connect with top talent',
    },
    {
        id: 'companies',
        label: 'Companies',
        href: MARKETING_URLS.companies,
        icon: Building2,
        description: 'Hire for your team',
    },
    {
        id: 'saas',
        label: 'Tool',
        href: MARKETING_URLS.home,
        icon: Wrench,
        description: 'Recruitment platform',
    },
    {
        id: 'agencies',
        label: 'For Agencies',
        href: MARKETING_URLS.agencies,
        icon: Handshake,
        description: 'Expand your coverage with CrossPath network',
    },
    {
        id: 'eor',
        label: 'For EOR',
        href: MARKETING_URLS.eorWhitelabel,
        icon: Globe,
        description: 'White-label recruitment layer',
    },
];

export const MARKETING_PARTNER_ITEMS: MarketingNavItem[] = [
    {
        label: 'Affiliate Program',
        href: MARKETING_URLS.affiliateProgram,
        icon: DollarSign,
        description: 'Earn commissions on tool referrals',
    },
    {
        label: 'Deal Partner',
        href: MARKETING_URLS.dealPartner,
        icon: Users,
        description: 'Partner on recruitment mandates',
    },
    {
        label: 'Affiliates API',
        href: MARKETING_URLS.affiliatesApi,
        icon: Code,
        description: 'Create affiliate signup links',
    },
    {
        label: 'Recruitment API',
        href: MARKETING_URLS.recruitmentApi,
        icon: Briefcase,
        description: 'Sync companies and missions',
    },
];

export const MARKETING_FOOTER_GROUPS: MarketingFooterGroup[] = [
    {
        title: 'Product',
        links: [
            { label: 'Recruitment Tool', href: MARKETING_URLS.home },
            { label: 'Recruiter Marketplace', href: MARKETING_URLS.platform },
            { label: 'Request a Demo', href: MARKETING_URLS.saasDemo },
            { label: 'Privacy', href: MARKETING_URLS.privacy },
        ],
    },
    {
        title: 'Solutions',
        links: MARKETING_SOLUTION_ITEMS.map(({ label, href }) => ({
            label,
            href,
        })),
    },
    {
        title: 'Partners',
        links: [
            ...MARKETING_PARTNER_ITEMS.map(({ label, href }) => ({
                label,
                href,
            })),
            { label: 'Terms', href: MARKETING_URLS.terms },
        ],
    },
];

/*----------------------------------
- HELPERS
----------------------------------*/

export const buildContactHref = ({ topic, source }: MarketingContactHrefOptions = {}): string => {
    const params = new URLSearchParams();

    if (topic) params.set('topic', topic);
    if (source) params.set('source', source);

    const queryString = params.toString();

    return queryString ? `${MARKETING_URLS.contact}?${queryString}` : MARKETING_URLS.contact;
};
